import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { isSupabaseConfigured, supabase } from '../../utils/supabaseClient';
import {
  deleteEpisode,
  fetchAllEpisodes,
  getFgtnSession,
  signInFgtnAdmin,
  signOutFgtnAdmin,
  upsertEpisode,
} from '../../utils/fgtnApi';
import {
  formatAirDate,
  getFgtnEpisodePath,
  isProbablyMissingTableError,
  sanitizeFgtnHtml,
  todayIsoDate,
} from '../../utils/fgtn';
import FgtnNotesEditor from './FgtnNotesEditor';
import FgtnQrBlock from './FgtnQrBlock';
import { FGTN_SETUP_SQL } from '../../data/fgtnSetupSql';
import './Fgtn.css';

const EMPTY_FORM = {
  id: null,
  episode_number: '',
  title: '',
  air_date: todayIsoDate(),
  body_html: '',
  published: true,
};

export default function FgtnAdminPage() {
  const [session, setSession] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [signingIn, setSigningIn] = useState(false);

  const [form, setForm] = useState(EMPTY_FORM);
  const [episodes, setEpisodes] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedEpisode, setSavedEpisode] = useState(null);
  const [episodesLoaded, setEpisodesLoaded] = useState(false);

  useEffect(() => {
    document.title = 'FGTN Admin | Angler Built';
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured() || !supabase) {
      setAuthReady(true);
      return undefined;
    }

    let cancelled = false;
    getFgtnSession()
      .then((nextSession) => {
        if (!cancelled) setSession(nextSession);
      })
      .catch((err) => {
        console.error('FGTN session check failed', err);
      })
      .finally(() => {
        if (!cancelled) setAuthReady(true);
      });

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });

    return () => {
      cancelled = true;
      data?.subscription?.unsubscribe();
    };
  }, []);

  const loadEpisodes = async () => {
    try {
      const rows = await fetchAllEpisodes();
      setEpisodes(rows);
      setLoadError('');
      setEpisodesLoaded(true);
      return rows;
    } catch (err) {
      console.error('Failed to load FGTN admin episodes', err);
      setLoadError(
        isProbablyMissingTableError(err)
          ? 'missing-table'
          : (err.message || 'Could not load episodes.')
      );
      setEpisodesLoaded(true);
      return [];
    }
  };

  useEffect(() => {
    if (!session) return undefined;
    loadEpisodes();
    return undefined;
  }, [session]);

  const nextEpisodeNumber = useMemo(() => {
    const max = episodes.reduce((highest, row) => Math.max(highest, row.episode_number || 0), 0);
    return max + 1;
  }, [episodes]);

  useEffect(() => {
    if (!session || !episodesLoaded || form.id) return;
    setForm((prev) => {
      if (prev.id || String(prev.episode_number).trim() !== '') return prev;
      return { ...prev, episode_number: String(nextEpisodeNumber) };
    });
  }, [session, episodesLoaded, nextEpisodeNumber, form.id]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setSigningIn(true);
    setAuthError('');
    try {
      await signInFgtnAdmin(email.trim(), password);
    } catch (err) {
      setAuthError(err.message || 'Could not sign in.');
    } finally {
      setSigningIn(false);
    }
  };

  const handleLogout = async () => {
    await signOutFgtnAdmin();
    setSavedEpisode(null);
    setEpisodes([]);
    setEpisodesLoaded(false);
    setForm({ ...EMPTY_FORM, air_date: todayIsoDate() });
  };

  const startNewEpisode = () => {
    setSavedEpisode(null);
    setSaveError('');
    setForm({
      ...EMPTY_FORM,
      air_date: todayIsoDate(),
      episode_number: String(nextEpisodeNumber),
    });
  };

  const loadEpisodeIntoForm = (episode) => {
    setSavedEpisode(null);
    setSaveError('');
    setForm({
      id: episode.id,
      episode_number: String(episode.episode_number),
      title: episode.title || '',
      air_date: episode.air_date,
      body_html: episode.body_html || '',
      published: episode.published !== false,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setSaveError('');
    setSavedEpisode(null);

    const episodeNumber = Number.parseInt(String(form.episode_number), 10);
    if (!Number.isInteger(episodeNumber) || episodeNumber < 1) {
      setSaveError('Enter a whole episode number, like 142.');
      setSaving(false);
      return;
    }
    if (!form.title.trim()) {
      setSaveError('Add an episode title.');
      setSaving(false);
      return;
    }
    if (!form.air_date) {
      setSaveError('Pick the show date.');
      setSaving(false);
      return;
    }

    try {
      const saved = await upsertEpisode({
        id: form.id,
        episode_number: episodeNumber,
        title: form.title.trim(),
        air_date: form.air_date,
        body_html: sanitizeFgtnHtml(form.body_html),
        published: form.published,
      });
      setForm((prev) => ({ ...prev, id: saved.id, body_html: saved.body_html }));
      setSavedEpisode(saved);
      await loadEpisodes();
    } catch (err) {
      console.error('Failed to save FGTN episode', err);
      if (isProbablyMissingTableError(err)) {
        setLoadError('missing-table');
        setSaveError('The FGTN table is missing in Supabase. Run the SQL below once, then try again.');
      } else if (err.code === '23505') {
        setSaveError(`Episode ${episodeNumber} already exists. Open it from Past episodes to edit.`);
      } else {
        setSaveError(err.message || 'Could not save this episode.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (episode) => {
    const ok = window.confirm(`Delete episode ${episode.episode_number}? This cannot be undone.`);
    if (!ok) return;
    const wasCurrent = form.id === episode.id;
    try {
      await deleteEpisode(episode.id);
      const rows = await loadEpisodes();
      if (wasCurrent) {
        const max = rows.reduce((highest, row) => Math.max(highest, row.episode_number || 0), 0);
        setSavedEpisode(null);
        setSaveError('');
        setForm({
          ...EMPTY_FORM,
          air_date: todayIsoDate(),
          episode_number: String(max + 1),
        });
      }
    } catch (err) {
      setSaveError(err.message || 'Could not delete that episode.');
    }
  };

  if (!isSupabaseConfigured()) {
    return (
      <div className="fgtn-page">
        <h1 className="fgtn-title">FGTN Admin</h1>
        <div className="fgtn-error">Supabase is not configured in this environment, so episodes cannot be saved yet.</div>
      </div>
    );
  }

  if (!authReady) {
    return (
      <div className="fgtn-page">
        <div className="fgtn-status">Checking login…</div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="fgtn-page">
        <div className="fgtn-card fgtn-login">
          <p className="fgtn-kicker">Private</p>
          <h1 className="fgtn-title" style={{ fontSize: 28 }}>FGTN Admin</h1>
          <p className="fgtn-subtitle">
            Sign in to publish this week’s show links. Use the email and password for the admin user in Supabase → Authentication → Users.
          </p>
          <form className="fgtn-form" onSubmit={handleLogin}>
            <div className="fgtn-field">
              <label htmlFor="fgtn-email">Email</label>
              <input
                id="fgtn-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="fgtn-field">
              <label htmlFor="fgtn-password">Password</label>
              <input
                id="fgtn-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {authError && <div className="fgtn-error">{authError}</div>}
            <button className="fgtn-btn" type="submit" disabled={signingIn}>
              {signingIn ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="fgtn-page">
      <div className="fgtn-admin-toolbar">
        <div>
          <p className="fgtn-kicker">Private</p>
          <h1 className="fgtn-title" style={{ marginBottom: 0 }}>FGTN Admin</h1>
        </div>
        <button type="button" className="fgtn-btn-secondary" onClick={handleLogout} style={{ width: 'auto' }}>
          Sign out
        </button>
      </div>

      {loadError === 'missing-table' && (
        <div className="fgtn-error" style={{ marginBottom: 20 }}>
          <p style={{ marginTop: 0 }}>
            The FGTN table is missing. Paste this SQL into the Supabase SQL editor once, then refresh this page.
          </p>
          <button
            type="button"
            className="fgtn-btn-secondary"
            style={{ width: 'auto', marginBottom: 12 }}
            onClick={() => navigator.clipboard.writeText(FGTN_SETUP_SQL)}
          >
            Copy setup SQL
          </button>
          <pre className="fgtn-setup">{FGTN_SETUP_SQL}</pre>
        </div>
      )}

      <div className="fgtn-admin-grid">
        <form className="fgtn-card fgtn-form" onSubmit={handleSave}>
          <div className="fgtn-form-row">
            <div className="fgtn-field">
              <label htmlFor="fgtn-number">Episode number</label>
              <input
                id="fgtn-number"
                type="number"
                min="1"
                step="1"
                value={form.episode_number}
                onChange={(e) => setForm((prev) => ({ ...prev, episode_number: e.target.value }))}
                required
              />
            </div>
            <div className="fgtn-field">
              <label htmlFor="fgtn-date">Date</label>
              <input
                id="fgtn-date"
                type="date"
                value={form.air_date}
                onChange={(e) => setForm((prev) => ({ ...prev, air_date: e.target.value }))}
                required
              />
            </div>
            <div className="fgtn-field">
              <label htmlFor="fgtn-title">Title</label>
              <input
                id="fgtn-title"
                type="text"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="What's in this week's show"
                required
              />
            </div>
          </div>

          <div className="fgtn-field">
            <label>Show notes / links</label>
            <FgtnNotesEditor
              value={form.body_html}
              onChange={(body_html) => setForm((prev) => ({ ...prev, body_html }))}
            />
            <p className="fgtn-help">
              Paste straight from OneNote. Hyperlinks stay intact, and bare URLs become links. The public page is
              {' '}
              anglerbuilt.com/fgtn/{form.episode_number || '142'}.
            </p>
          </div>

          <label className="fgtn-check">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm((prev) => ({ ...prev, published: e.target.checked }))}
            />
            Published (visible on /fgtn)
          </label>

          {saveError && <div className="fgtn-error">{saveError}</div>}

          <div className="fgtn-actions" style={{ flexDirection: 'row' }}>
            <button className="fgtn-btn" type="submit" disabled={saving}>
              {saving ? 'Saving…' : form.id ? 'Save episode' : 'Publish episode'}
            </button>
            <button className="fgtn-btn-secondary" type="button" onClick={startNewEpisode}>
              New episode
            </button>
          </div>
        </form>

        <div>
          {savedEpisode && (
            <div style={{ marginBottom: 16 }}>
              <FgtnQrBlock episodeNumber={savedEpisode.episode_number} title={savedEpisode.title} />
              <p className="fgtn-help">
                Live page:{' '}
                <Link to={getFgtnEpisodePath(savedEpisode.episode_number)}>
                  /fgtn/{savedEpisode.episode_number}
                </Link>
              </p>
            </div>
          )}

          <div className="fgtn-card">
            <h2 style={{ marginTop: 0, fontSize: 16 }}>Past episodes</h2>
            {episodes.length === 0 && <p className="fgtn-help">None yet. Publish the first one with the form.</p>}
            <div className="fgtn-episode-list fgtn-admin-list">
              {episodes.map((episode) => (
                <div key={episode.id} className="fgtn-episode-row" style={{ cursor: 'default' }}>
                  <div className="fgtn-episode-number">Ep {episode.episode_number}</div>
                  <div className="fgtn-episode-row-body" style={{ flex: 1 }}>
                    <h3 className="fgtn-episode-row-title">{episode.title}</h3>
                    <p className="fgtn-episode-row-date">
                      {formatAirDate(episode.air_date)}
                      {!episode.published ? ' · hidden' : ''}
                    </p>
                    <div className="fgtn-actions" style={{ flexDirection: 'row', marginTop: 8 }}>
                      <button type="button" className="fgtn-btn-secondary" onClick={() => loadEpisodeIntoForm(episode)}>
                        Edit
                      </button>
                      <button type="button" className="fgtn-btn-danger" onClick={() => handleDelete(episode)}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
